import { formatDate } from "@/lib/formatters/DateFormatter";
import { FormModelChangeHandler } from "@/types/forms/types";
import { testIdConstants } from "@/types/test-id-constants/testIdConstants";
import {
  PriceCalculatorFormData,
  PriceCalculatorFormDataDefaultState,
} from "@/features/price-calculator/types/types";
import { PriceCalculatorAllMultiLegRoutesQuery } from "@/gql/graphql";
import { gql, useQuery } from "@apollo/client";
import { Box, Row, Space, Text } from "@stenajs-webui/core";
import { Select } from "@stenajs-webui/select";
import * as React from "react";
import { useMemo } from "react";
import {
  MultiLegOption,
  RouteLeg,
} from "@/features/price-calculator/types/customer-form-types";

const query = gql`
  query PriceCalculatorAllMultiLegRoutes {
    productPrice {
      route {
        allMultiLeg {
          id
          name
          code
          routeLegs {
            id
            legNo
            legRoute {
              code
              id
              name
            }
          }
        }
      }
    }
  }
`;

interface Props {
  formData: PriceCalculatorFormData;
  onChangeFormData: FormModelChangeHandler<PriceCalculatorFormData>;
  defaultFormState: PriceCalculatorFormDataDefaultState;
}

export const transformToRouteLegs = (routeLegs: RouteLeg[]) =>
  routeLegs.map((routeLeg) => ({
    routeCode: routeLeg.legRoute.code,
    sailingTypeCode: "P",
    departureDate: formatDate(new Date().toISOString()),
    departureTime: "11:11",
  }));

export const PriceCalculatorSelectMultiLegRoute: React.FC<Props> = ({
  formData,
  onChangeFormData,
  defaultFormState,
}) => {
  const { loading, data } =
    useQuery<PriceCalculatorAllMultiLegRoutesQuery>(query);

  const multiLegRoutes = useMemo(() => {
    return data?.productPrice?.route?.allMultiLeg;
  }, [data]);

  const options = useMemo<MultiLegOption[] | undefined>(() => {
    return multiLegRoutes?.map((r) => ({
      label: r.code + " - " + r.name,
      value: r.code,
      data: r,
    }));
  }, [multiLegRoutes]);

  const selected = useMemo(
    () => options?.find((o) => o.value === formData.routeCode) || null,
    [options, formData.routeCode]
  );

  return (
    <Row alignItems={"center"}>
      <Text>Route</Text>
      <Space />
      <Box
        width={"240px"}
        data-testid={testIdConstants.priceCalculatorMultilegRouteSelect}
      >
        <Select
          isLoading={loading}
          options={options}
          isClearable
          value={selected}
          onChange={(value) => {
            onChangeFormData("multiLegRouteData", value?.data);
            onChangeFormData("routeCode", value?.data.code);
            value?.data?.routeLegs &&
              onChangeFormData(
                "routeLegs",
                transformToRouteLegs(value.data.routeLegs)?.map(
                  (routeLeg, index) => {
                    return routeLeg.routeCode ===
                      value.data.routeLegs?.[index].legRoute.code
                      ? {
                          ...routeLeg,
                          sailingTypeCode: defaultFormState.sailingType ?? "",
                          departureTime: defaultFormState.departureTime ?? "",
                          departureTimeManual: true,
                        }
                      : routeLeg;
                  }
                )
              );
          }}
          menuPortalTarget={document.body}
        />
      </Box>
    </Row>
  );
};
